import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
	AlertTriangle,
	Ban,
	CalendarClock,
	CalendarDays,
	Car,
	CircleDollarSign,
	Clock3,
	Hourglass,
	ListFilter,
	MoveHorizontal,
	PencilLine,
	Receipt,
	Repeat,
	Scissors,
	Timer,
	TimerOff,
	TrendingUp,
	UserRound,
	Users,
} from "lucide-react";
import InfoHint, { HintBubble, useHintDismiss, usePosition } from "../infoHint/infoHint";
import RowsModal from "../rowsModal/rowsModal";
import {
	formatBucket,
	formatDuration,
	formatHour,
	formatMoney,
	formatMoneyShort,
	formatNotice,
	fill,
} from "../reportsFormat";

/* Drawn inline rather than through a chart library — the app carries no charting
   dependency and this is a single series. */
const CHART_H = 200;
const AXIS_W = 52;
const RIGHT_PAD = 8;
const MAX_BAR_W = 44;
const MAX_LABELS = 11;
const LABEL_MIN_W = 48;  // rough width of a "12 Mar" tick label

/* How many rows a ranked panel shows before the rest go behind "Show all".

   Three of these panels sit in one row, so their height is the page's height. A
   shop with ten staff and a long service list was turning each card into a
   column taller than the viewport — the report grew with the business until the
   thing it was measuring made it unreadable. Six rows covers the question these
   cards actually answer ("who and what is carrying this period"); the ranking's
   tail is a lookup, and lookups belong in a dialog. */
const PANEL_ROWS = 6;

/* The chart used to render at a width derived from the bucket count, which
   overflowed its card and got clipped on the right. It now measures the space it
   actually has and lays the bars out to fill exactly that, so it fits at any
   period length and re-fits when the window resizes. */
function useMeasuredWidth() {
	const ref = useRef(null);
	const [width, setWidth] = useState(0);

	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return undefined;

		const measure = () => setWidth(el.clientWidth);
		measure();

		// Guard for older browsers rather than assuming ResizeObserver exists.
		if (typeof ResizeObserver === "undefined") {
			window.addEventListener("resize", measure);
			return () => window.removeEventListener("resize", measure);
		}

		const ro = new ResizeObserver(measure);
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	return [ref, width];
}

const RevenueChart = ({ series, granularity, currency, language, t }) => {
	const [wrapRef, width] = useMeasuredWidth();

	/* Each bar carried a native <title>, which is the same control-that-behaves-
	   like-decoration problem InfoHint was written to solve: a second of hover
	   before anything appears, and nothing at all on a touch screen. The bars
	   now open the very same bubble — hover to preview, tap or click to pin. */
	const [active, setActive] = useState(null);
	const [pinned, setPinned] = useState(false);
	const anchorRef = useRef(null);
	const bubbleRef = useRef(null);
	const open = active !== null;
	// `active` doubles as the re-place key: the anchor element changes per bar
	// while the bubble stays open.
	const pos = usePosition(open, anchorRef, active);

	const close = useCallback(() => {
		setActive(null);
		setPinned(false);
	}, []);

	/* Dismissal is scoped to the whole chart, not to the anchored bar. Anchoring
	   it to the bar meant a press on the column's hit area — which is not inside
	   the bar rect — counted as "outside", so the capture-phase close fired a
	   beat before the click that was meant to pin it. The chart owns presses
	   within itself; anywhere else closes. */
	useHintDismiss(open, close, wrapRef, bubbleRef);

	/* Anchored to the bar rather than the column, so the bubble sits just above
	   the value it describes instead of below the full-height hit area. A bucket
	   that earned nothing has no bar, and falls back to the column. */
	const anchorFrom = (el) => el.querySelector(".sf-rap-bar") || el;

	const activePoint = open ? series[active] : null;
	const max = useMemo(() => Math.max(...series.map((p) => p.revenue), 0), [series]);

	// The series now spans the whole period, so an empty one means an empty range.
	const hasData = series.length > 0 && max > 0;

	/* Reserve the height on the first paint, before the width is known, so the
	   card does not jump once the measurement lands. */
	const layout = useMemo(() => {
		if (!width || series.length === 0) return null;

		const innerW = Math.max(width - AXIS_W - RIGHT_PAD, 10);
		const slot = innerW / series.length;
		const barW = Math.max(Math.min(slot - 4, MAX_BAR_W), 1);

		/* Label density follows the available width, not just the bucket count —
		   eleven "12 Mar"-sized labels fit a desktop card but collide on a phone. */
		const fitsLabels = Math.max(Math.floor(innerW / LABEL_MIN_W), 2);
		const labelStep = Math.ceil(series.length / Math.min(MAX_LABELS, fitsLabels));

		return { innerW, slot, barW, labelStep };
	}, [width, series.length]);

	return (
		<div className="sf-rap-chart-wrap" ref={wrapRef}>
			{!hasData && <p className="sf-rap-empty">{t.chart.empty}</p>}

			{hasData && layout && (
				<svg
					className="sf-rap-chart"
					width={width}
					height={CHART_H + 30}
					role="img"
					aria-label={t.chart.title}
					/* Closing on the chart's edge rather than each bar's keeps the
					   bubble steady while the pointer travels along the series. */
					onMouseLeave={() => {
						if (!pinned) setActive(null);
					}}
				>
					<defs>
						<linearGradient id="sf-rap-bar-grad" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor="#5ecaf5" />
							<stop offset="100%" stopColor="#1a5f8a" />
						</linearGradient>
						<linearGradient id="sf-rap-bar-grad-hot" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor="#ffc45c" />
							<stop offset="100%" stopColor="#f5a623" />
						</linearGradient>
					</defs>

					{[0, 0.5, 1].map((f) => {
						const y = CHART_H - f * CHART_H;
						return (
							<g key={f}>
								<line className="sf-rap-grid-line" x1={AXIS_W} y1={y} x2={width - RIGHT_PAD} y2={y} />
								<text className="sf-rap-grid-text" x={AXIS_W - 10} y={y + 3} textAnchor="end">
									{formatMoneyShort(max * f, currency)}
								</text>
							</g>
						);
					})}

					{series.map((point, i) => {
						const h = point.revenue > 0 ? Math.max((point.revenue / max) * CHART_H, 3) : 0;
						const x = AXIS_W + i * layout.slot + (layout.slot - layout.barW) / 2;
						const label = formatBucket(point.period, granularity, language);

						return (
							<g
								key={point.period}
								className={`sf-rap-bar-col ${active === i ? "is-active" : ""}`}
								onMouseEnter={(e) => {
									if (pinned) return;
									anchorRef.current = anchorFrom(e.currentTarget);
									setActive(i);
								}}
								onClick={(e) => {
									e.stopPropagation();
									anchorRef.current = anchorFrom(e.currentTarget);
									// A second press on the same bar releases it.
									if (pinned && active === i) close();
									else {
										setActive(i);
										setPinned(true);
									}
								}}
							>
								{/* Full-height hit area, so a quiet day is still reachable */}
								<rect
									className="sf-rap-bar-hit"
									x={AXIS_W + i * layout.slot}
									y={0}
									width={layout.slot}
									height={CHART_H}
								/>

								{h > 0 && (
									<rect
										className="sf-rap-bar"
										x={x}
										y={CHART_H - h}
										width={layout.barW}
										height={h}
										rx={Math.min(4, layout.barW / 3)}
									/>
								)}

								{i % layout.labelStep === 0 && (
									<text
										className="sf-rap-bar-label"
										x={x + layout.barW / 2}
										y={CHART_H + 18}
										textAnchor="middle"
									>
										{label}
									</text>
								)}
							</g>
						);
					})}
				</svg>
			)}

			{open && activePoint && (
				<HintBubble
					pos={pos}
					pinned={pinned}
					onClose={close}
					label={t.info}
					bubbleRef={bubbleRef}
				>
					<p className="sf-rap-hint-head">{formatBucket(activePoint.period, granularity, language)}</p>
					<p>
						{formatMoney(activePoint.revenue, currency)} · {activePoint.bookings} {t.chart.bookingsLabel}
					</p>
				</HintBubble>
			)}
		</div>
	);
};

/* The tail of a capped list, when the server folded it into one row. Named
   rather than left blank so the card still adds up to the headline total. */
const isOtherRow = (row) => Boolean(row?.isOther);

/* Shared shell for the two ranked-list panels below.

   Both show PANEL_ROWS rows and put the remainder behind a dialog, so neither
   can grow the page without bound. The footer says how many are hidden rather
   than just "show all", because the count is the useful part: "+22 more" tells
   an owner their catalog is long before they open anything. */
const RankPanel = ({ icon, title, hint, rows, empty, renderRow, nameOf, t, footNote, limit = PANEL_ROWS }) => {
	const [open, setOpen] = useState(false);
	const shown = rows.slice(0, limit);
	const hidden = rows.length - shown.length;

	return (
		<section className="sf-rap-panel">
			<h3 className="sf-rap-panel-title">
				{icon}
				<span>{title}</span>
				{hint && <InfoHint text={hint} label={t.info} />}
			</h3>

			{rows.length === 0 ? (
				<p className="sf-rap-empty">{empty}</p>
			) : (
				<>
					<ol className="sf-rap-rank-list">{shown.map((row, i) => renderRow(row, i))}</ol>

					{hidden > 0 && (
						<button type="button" className="sf-rap-more" onClick={() => setOpen(true)}>
							<ListFilter size={14} />
							{fill(t.breakdown.showAll, { count: rows.length })}
							<em>{fill(t.breakdown.andMore, { count: hidden })}</em>
						</button>
					)}

					{footNote && <p className="sf-rap-panel-note">{footNote}</p>}
				</>
			)}

			{open && (
				<RowsModal
					title={title}
					rows={rows}
					total={fill(t.breakdown.rowCount, { count: rows.length })}
					renderRow={renderRow}
					nameOf={nameOf}
					onClose={() => setOpen(false)}
					t={t}
				/>
			)}
		</section>
	);
};

/* A ranked list rather than a table: three of these sit side by side, so a
   four-column table gets squeezed until the share bar wraps out of its cell.
   Name and amount share a row, the bar sits under it with the full width. */
const BreakdownPanel = ({ icon, title, hint, rows, total, currency, t, nameOf }) => {
	const label = (row) => (isOtherRow(row) ? t.breakdown.other : nameOf(row));

	const renderRow = (row, i) => {
		const share = total > 0 ? (row.revenue / total) * 100 : 0;
		const other = isOtherRow(row);

		return (
			<li key={`${label(row)}-${i}`} className={other ? "is-other" : ""}>
				<div className="sf-rap-rank-row">
					<i className={`sf-rap-rank ${i === 0 && !other ? "is-top" : ""}`}>{other ? "…" : i + 1}</i>
					<span className="sf-rap-rank-name" title={label(row)}>
						{label(row)}
					</span>
					<strong className="sf-rap-rank-amount">{formatMoney(row.revenue, currency)}</strong>
				</div>
				<div className="sf-rap-rank-meter">
					<span className="sf-rap-share-track">
						<span
							className={`sf-rap-share-bar ${i === 0 && !other ? "is-top" : ""}`}
							style={{ width: `${Math.min(share, 100)}%` }}
						/>
					</span>
					<span className="sf-rap-rank-meta">
						{other
							? fill(t.breakdown.otherSub, { count: row.mergedRows })
							: `${row.bookings} ${t.breakdown.bookings.toLowerCase()} · ${share.toFixed(0)}%`}
					</span>
				</div>
			</li>
		);
	};

	return (
		<RankPanel
			icon={icon}
			title={title}
			hint={hint}
			rows={rows}
			empty={t.breakdown.empty}
			renderRow={renderRow}
			nameOf={label}
			t={t}
		/>
	);
};

const Kpi = ({ icon, label, value, hint, sub, primary, t }) => (
	<div className={`sf-rap-kpi ${primary ? "is-primary" : ""}`}>
		<div className="sf-rap-kpi-top">
			<span className="sf-rap-kpi-icon">{icon}</span>
			<span className="sf-rap-kpi-label">{label}</span>
			{hint && <InfoHint text={hint} label={t?.info} />}
		</div>
		<span className="sf-rap-kpi-value">{value}</span>
		{sub && <span className="sf-rap-kpi-sub">{sub}</span>}
	</div>
);

/* A ranked-list sibling for counts rather than money.

   Bars are sized against the BUSIEST row, not the total: on "busiest hours" the
   question is which hour is your peak, and shares of a total render every bar
   as a stub once the day is spread across ten of them.

   `ordered` keeps a list in its natural order (days of the week, notice bands)
   rather than ranking it — the shape of the distribution is the point there, so
   no rank badge and no "top" highlight. */
const CountPanel = ({ icon, title, hint, rows, nameOf, empty, meta, t, ordered = false, footNote }) => {
	const max = rows.reduce((m, r) => Math.max(m, r.count ?? r.bookings ?? 0), 0);
	const valueOf = (row) => row.count ?? row.bookings ?? 0;

	const renderRow = (row, i) => {
		const value = valueOf(row);
		const isTop = !ordered && max > 0 && value === max;

		return (
			<li key={`${nameOf(row)}-${i}`}>
				<div className="sf-rap-rank-row">
					<span className="sf-rap-rank-name" title={nameOf(row)}>
						{nameOf(row)}
					</span>
					<strong className="sf-rap-rank-amount">{value}</strong>
				</div>
				<div className="sf-rap-rank-meter sf-rap-count-meter">
					<span className="sf-rap-share-track">
						<span
							className={`sf-rap-share-bar ${isTop ? "is-top" : ""}`}
							style={{ width: `${max > 0 ? (value / max) * 100 : 0}%` }}
						/>
					</span>
					{meta && <span className="sf-rap-rank-meta">{meta(row)}</span>}
				</div>
			</li>
		);
	};

	return (
		<RankPanel
			icon={icon}
			title={title}
			hint={hint}
			rows={rows}
			empty={empty}
			renderRow={renderRow}
			nameOf={nameOf}
			t={t}
			footNote={footNote}
			/* An ordered list is a distribution, and half a distribution is a
			   different claim from the whole one: capping the days of the week at
			   six hid Sunday, and capping the notice bands would have dropped the
			   longest one — the reassuring end of the chart. They are all bounded
			   by nature (seven days, five bands), so there is nothing to cap. */
			limit={ordered ? rows.length : undefined}
		/>
	);
};

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

/* Volume rather than money: how busy the shop was, which the page said nothing
   about before. Every figure here is SCHEDULED time — nothing records when a
   service actually began or ended, so a booking that overran, finished early or
   was never attended still contributes its full planned slot. */
const BookingsSection = ({ volume, uniqueCustomers, t }) => {
	if (!volume) return null;

	const d = t.bookings;
	const active = volume.concluded + volume.upcoming;
	const visitsPerClient = uniqueCustomers > 0 ? (active / uniqueCustomers).toFixed(1) : "—";

	/* Denominator for the hour shares: the bookings that landed in some hour, not
	   volume.total — anything with no usable start time is absent from byHour and
	   would otherwise make the column add up to less than 100%. */
	const hourBookings = volume.byHour.reduce((sum, r) => sum + r.bookings, 0);

	return (
		<section className="sf-rap-section">
			<div className="sf-shop-section-head">
				<h2 className="sf-shop-h2">{d.title}</h2>
				<span className="sf-rap-head-note">{d.subtitle}</span>
			</div>

			<div className="sf-rap-kpis">
				<Kpi
					t={t}
					icon={<CalendarDays size={15} />}
					label={d.total}
					hint={d.totalHint}
					value={volume.total}
					sub={fill(d.totalSub, {
						concluded: volume.concluded,
						upcoming: volume.upcoming,
						cancelled: volume.cancelled,
					})}
				/>
				<Kpi
					t={t}
					icon={<Hourglass size={15} />}
					label={d.hours}
					hint={d.hoursHint}
					value={formatDuration(volume.scheduledMinutes, d.duration)}
					sub={fill(d.hoursSub, { done: formatDuration(volume.concludedMinutes, d.duration) })}
				/>
				<Kpi
					t={t}
					icon={<Timer size={15} />}
					label={d.averageLength}
					hint={d.averageLengthHint}
					value={formatDuration(volume.averageLengthMinutes, d.duration)}
					sub={fill(d.averageLengthSub, {
						median: formatDuration(volume.medianLengthMinutes, d.duration),
					})}
				/>
				<Kpi
					t={t}
					icon={<CalendarClock size={15} />}
					label={d.leadTime}
					hint={d.leadTimeHint}
					value={formatDuration(volume.leadTime.medianMinutes, d.duration, { preferDays: true })}
					sub={d.leadTimeSub}
				/>
				<Kpi
					t={t}
					icon={<Repeat size={15} />}
					label={d.visitsPerClient}
					hint={d.visitsPerClientHint}
					value={visitsPerClient}
					sub={fill(d.visitsPerClientSub, { clients: uniqueCustomers })}
				/>
			</div>

			<div className="sf-rap-grid">
				{/* Monday-first and never reordered by size — a quiet Wednesday
				    between two busy days is the point of looking. */}
				<CountPanel
					t={t}
					ordered
					icon={<CalendarDays size={16} />}
					title={d.byDay}
					rows={volume.byDayOfWeek}
					empty={d.empty}
					nameOf={(r) => d.days[DAY_KEYS[r.day]]}
					meta={(r) => formatDuration(r.minutes, d.duration)}
				/>
				{/* Ranked, not chronological: the peak is the answer here, and a
				    shop open twelve hours has twelve rows — so this is the panel
				    that most needs the show-all footer.

				    The share is not decoration. Without a meta line these rows
				    were a line shorter than the ones in "Busiest days" beside
				    them, so two panels showing the same kind of thing did not line
				    up — and a share is the figure that answers "is 12 a lot?"
				    when the bar is drawn against the peak rather than the total. */}
				<CountPanel
					t={t}
					icon={<Clock3 size={16} />}
					title={d.byHour}
					rows={volume.byHour}
					empty={d.empty}
					nameOf={(r) => formatHour(r.hour)}
					meta={(r) => fill(d.hourShare, { share: hourBookings > 0 ? Math.round((r.bookings / hourBookings) * 100) : 0 })}
				/>

				{/* "Bookings by source" used to sit here as a third panel. It was a
				    count-only copy of the by-source breakdown under "Where the
				    money came from" — same three rows, one card apart, disagreeing
				    on the number because one counted revenue-earning bookings and
				    the other counted all of them. The money one survives; it is
				    the one that says something the rest of this page does not. */}

				{/* Only for shops that travel. Their transit time is real cost and
				    nothing else on this page accounts for it. */}
				{volume.homeService && (
					<section className="sf-rap-panel">
						<h3 className="sf-rap-panel-title">
							<Car size={16} />
							<span>{d.homeVisits}</span>
						</h3>
						<ol className="sf-rap-rank-list">
							<li>
								<div className="sf-rap-rank-row">
									<span className="sf-rap-rank-name">{d.homeVisitsCount}</span>
									<strong className="sf-rap-rank-amount">{volume.homeService.bookings}</strong>
								</div>
								<div className="sf-rap-rank-meter sf-rap-count-meter">
									<span className="sf-rap-share-track">
										<span
											className="sf-rap-share-bar"
											style={{ width: `${Math.min(volume.homeService.share, 100)}%` }}
										/>
									</span>
									<span className="sf-rap-rank-meta">
										{fill(d.homeVisitsShare, { share: volume.homeService.share })}
									</span>
								</div>
							</li>
							<li>
								<div className="sf-rap-rank-row">
									<span className="sf-rap-rank-name">{d.travelTime}</span>
									<strong className="sf-rap-rank-amount">
										{formatDuration(volume.homeService.transitMinutes, d.duration)}
									</strong>
								</div>
							</li>
						</ol>
					</section>
				)}
			</div>
		</section>
	);
};

/* The cancellation half of the screen.

   Two blocks, because the API reports two different things and merging them was
   the original bug: `cohort` is the outcome of the slots that sat in this
   period, `activity` is the cancelling that actually happened during it. A
   cancellation made today for next month's appointment only ever appears in the
   second one.

   Counts throughout. The "value released" figure this used to lead with is
   gone: it was money the shop never had, priced for older bookings off today's
   catalog, and it read as a loss on the books. What a cancellation costs you is
   the notice you got, which is what the panels below measure. */
const CancellationsSection = ({ cancellations, t }) => {
	const { cohort, activity } = cancellations || {};
	if (!cohort) return null;

	const notice = t.cancellations.notice;

	/* Lead-time bands stay in their natural order rather than being ranked, so
	   the shape of the distribution is readable. The "unknown" band is only shown
	   when something actually landed in it. */
	const leadRows = (activity?.byLeadTime || []).filter((row) => row.bucket !== "unknown" || row.count > 0);
	const actorRows = (activity?.byActor || []).filter((row) => row.count > 0);

	/* Null when too few of this period's appointments have concluded to make a
	   percentage mean anything. "0%" there would read as "nobody cancels", which
	   is the opposite of "we don't know yet". */
	const hasRate = cohort.rate !== null && cohort.rate !== undefined;

	const cohortKpi = (
		<Kpi
			t={t}
			icon={<Ban size={15} />}
			label={t.cancellations.cohortRate}
			hint={hasRate ? t.cancellations.cohortRateHint : t.cancellations.cohortRatePendingHint}
			value={hasRate ? `${cohort.rate}%` : "—"}
			sub={fill(t.cancellations.cohortRateSub, { cancelled: cohort.cancelled, settled: cohort.settled })}
		/>
	);

	return (
		<section className="sf-rap-section">
			<div className="sf-shop-section-head">
				<h2 className="sf-shop-h2">{t.cancellations.title}</h2>
				<span className="sf-rap-head-note">{t.cancellations.subtitle}</span>
			</div>

			{!activity ? (
				/* The cohort rate does not depend on the sparse index, so it still
				   has something to say when the activity query could not run. */
				<>
					<div className="sf-rap-kpis">{cohortKpi}</div>
					<div className="sf-rap-panel">
						<p className="sf-rap-empty">{t.cancellations.activityUnavailable}</p>
					</div>
				</>
			) : (
				<>
					<div className="sf-rap-kpis">
						<Kpi
							t={t}
							icon={<Ban size={15} />}
							label={t.cancellations.count}
							hint={t.cancellations.countHint}
							value={activity.count}
						/>
						<Kpi
							t={t}
							icon={<TimerOff size={15} />}
							label={t.cancellations.shortNotice}
							hint={t.cancellations.shortNoticeHint}
							value={activity.shortNoticeCount}
							sub={fill(t.cancellations.ofTotal, { total: activity.count })}
						/>
						<Kpi
							t={t}
							icon={<Clock3 size={15} />}
							label={t.cancellations.medianNotice}
							hint={t.cancellations.medianNoticeHint}
							value={formatNotice(activity.medianLeadMinutes, notice)}
						/>
						{/* The cohort rate belongs here rather than in the headline
						    row: it measures this period's SLOTS, so it moves on a
						    different clock to everything above it. */}
						{cohortKpi}
					</div>

					{/* "By staff" used to be a third panel here. It ranked people by
					    how often their customers changed their minds — something
					    none of them controls — and reading it as a performance
					    league table was the only thing it was good for. */}
					<div className="sf-rap-grid sf-rap-grid--two">
						<CountPanel
							t={t}
							ordered
							icon={<TimerOff size={16} />}
							title={t.cancellations.byLeadTime}
							rows={leadRows}
							empty={t.breakdown.empty}
							nameOf={(r) => t.cancellations.lead[r.bucket] || r.bucket}
						/>
						<CountPanel
							t={t}
							icon={<UserRound size={16} />}
							title={t.cancellations.byActor}
							rows={actorRows}
							empty={t.breakdown.empty}
							nameOf={(r) => t.cancellations.actors[r.actor] || r.actor}
						/>
					</div>
				</>
			)}
		</section>
	);
};

/* Bookings that were moved or edited after they were made.

   The page had no way to see these at all, so a calendar that was rewritten
   twice a week looked exactly like one nobody touched. It replaces the value
   released figure as the other half of "how much did this month churn" — and
   unlike that figure, it is something a shop can act on: changes coming in
   under two hours' notice are an availability problem, not an accounting one. */
const ModificationsSection = ({ modifications, t }) => {
	if (!modifications) return null;

	const m = t.modifications;
	const notice = t.cancellations.notice;
	const duration = t.bookings.duration;

	const hasRate = modifications.rate !== null && modifications.rate !== undefined;

	const noticeRows = (modifications.byNotice || []).filter((row) => row.bucket !== "unknown" || row.count > 0);
	const shiftRows = (modifications.byShift || []).filter((row) => row.count > 0);

	/* Two counts that belong to the whole panel rather than to any one band.
	   "Moved past these dates" is the figure the report could not produce at all
	   before changes were counted on the day they were made. */
	const moveNotes = [
		modifications.repeatedlyMoved > 0 ? `${m.repeated}: ${modifications.repeatedlyMoved}` : null,
		modifications.movedBeyondPeriod > 0 ? `${m.movedBeyond}: ${modifications.movedBeyondPeriod}` : null,
	].filter(Boolean);

	return (
		<section className="sf-rap-section">
			<div className="sf-shop-section-head">
				<h2 className="sf-shop-h2">{m.title}</h2>
				<span className="sf-rap-head-note">{m.subtitle}</span>
			</div>

			<div className="sf-rap-kpis">
				<Kpi
					t={t}
					icon={<PencilLine size={15} />}
					label={m.count}
					hint={m.countHint}
					value={modifications.count}
					sub={fill(m.countSub, { moved: modifications.rescheduled, edited: modifications.edited })}
				/>
				<Kpi
					t={t}
					icon={<Repeat size={15} />}
					label={m.rate}
					hint={hasRate ? m.rateHint : m.ratePendingHint}
					value={hasRate ? `${modifications.rate}%` : "—"}
					sub={fill(m.rateSub, { eligible: modifications.eligible })}
				/>
				<Kpi
					t={t}
					icon={<TimerOff size={15} />}
					label={m.shortNotice}
					hint={m.shortNoticeHint}
					value={modifications.shortNoticeCount}
					sub={fill(t.cancellations.ofTotal, { total: modifications.count })}
				/>
				<Kpi
					t={t}
					icon={<Clock3 size={15} />}
					label={m.medianNotice}
					hint={m.medianNoticeHint}
					value={formatNotice(modifications.medianNoticeMinutes, notice)}
				/>
				<Kpi
					t={t}
					icon={<MoveHorizontal size={15} />}
					label={m.typicalMove}
					hint={m.typicalMoveHint}
					value={formatDuration(modifications.medianShiftMinutes, duration, { preferDays: true })}
					sub={fill(m.typicalMoveSub, {
						forward: modifications.broughtForward,
						back: modifications.pushedBack,
					})}
				/>
			</div>

			{modifications.count === 0 ? (
				<div className="sf-rap-panel">
					<p className="sf-rap-empty">{m.none}</p>
				</div>
			) : (
				<div className="sf-rap-grid sf-rap-grid--two">
					<CountPanel
						t={t}
						ordered
						icon={<TimerOff size={16} />}
						title={m.byNotice}
						rows={noticeRows}
						empty={m.empty}
						nameOf={(r) => t.cancellations.lead[r.bucket] || r.bucket}
					/>
					<CountPanel
						t={t}
						ordered
						icon={<MoveHorizontal size={16} />}
						title={m.byMove}
						hint={m.byMoveHint}
						rows={shiftRows}
						empty={m.empty}
						nameOf={(r) => m.shift[r.bucket] || r.bucket}
						footNote={moveNotes.length > 0 ? moveNotes.join(" · ") : null}
					/>
				</div>
			)}
		</section>
	);
};

const SECTIONS = ["overview", "money", "bookings", "changes"];

const SalesPanel = ({ report, granularity: requested, language, t }) => {
	const { currency, totals, byService, byStaff, bySource, series, dataQuality, cancellations, bookingVolume, modifications } =
		report;

	/* The grouping THIS report was built with, not the one the picker is showing.

	   They disagree for exactly one render whenever Group by changes: setting the
	   state re-renders immediately, while the refetch it triggers only sets the
	   loading flag inside the effect that follows. In that frame the panel was
	   handed "month" alongside a still-day-bucketed series, and formatBucket
	   pasted "-01" onto a day bucket — which threw out of the chart's render and
	   took the whole page down with it. formatBucket is now proof against that on
	   its own, but the frame should be right as well as safe: reading the
	   grouping off the response means it renders the old chart correctly until
	   the new one lands. */
	const granularity = report.range?.granularity || requested;

	const sourceLabel = (row) => t.sources[row.source] || row.source;
	// Outcome-based now: a walk-in from last month is done, not pending.
	const pending = totals.bookings.upcoming;

	const activity = cancellations?.activity;

	/* On a phone this panel is a very long scroll, so below 900px only one
	   section shows at a time and this strip switches between them. Above that
	   the strip is hidden by CSS and every section renders as before — the
	   sections stay mounted either way, so the chart keeps its measured width
	   and nothing re-fetches when you switch. */
	const [section, setSection] = useState("overview");

	/* The honest part of this screen: how much of the headline was reconstructed
	   from today's prices rather than recorded at the time. Only rendered when
	   there is genuinely something to disclose. */
	const notices = [];
	if (dataQuality?.catalogBasisRevenue > 0) {
		notices.push(fill(t.quality.estimated, { amount: formatMoney(dataQuality.catalogBasisRevenue, currency) }));
	}
	if (dataQuality?.bookingsMissingPrice > 0) {
		notices.push(fill(t.quality.missing, { count: dataQuality.bookingsMissingPrice }));
	}
	if (dataQuality?.unpricedServices?.length > 0) {
		notices.push(
			fill(t.quality.unpriced, { list: dataQuality.unpricedServices.slice(0, 6).map((s) => s.serviceId).join(", ") }),
		);
	}
	if (dataQuality?.truncated) notices.push(t.quality.truncated);

	/* An owner reading "3 cancelled of 40 concluded" against a period they know
	   held 55 bookings will assume the report is broken unless the gap is named. */
	if (dataQuality?.cohortUnsettled > 0) {
		notices.push(fill(t.quality.unsettled, { count: dataQuality.cohortUnsettled }));
	}
	/* Revenue jumped when this started counting; say why rather than let an
	   owner discover a changed total on their own. */
	if (dataQuality?.concludedByElapsedTime > 0) {
		notices.push(fill(t.quality.concludedByTime, { count: dataQuality.concludedByElapsedTime }));
	}
	if (dataQuality?.bookingsMissingLength > 0) {
		notices.push(fill(t.quality.missingLength, { count: dataQuality.bookingsMissingLength }));
	}
	if (dataQuality?.cancellationActivityAvailable === false) {
		notices.push(t.quality.cancellationsUnavailable);
	}
	if (dataQuality?.cancellationsTruncated) notices.push(t.quality.cancellationsTruncated);
	/* Only when the forward scan could not run. With it, a change that pushed an
	   appointment beyond these dates is still counted here. */
	if (modifications && dataQuality?.modificationsForwardScan === false) {
		notices.push(t.quality.modificationsForwardUnavailable);
	}

	return (
		<>
			{/* Hidden above 900px, where every section is shown stacked. */}
			<nav className="sf-rap-subtabs" role="tablist" aria-label={t.sections.label}>
				{SECTIONS.map((key) => (
					<button
						key={key}
						type="button"
						role="tab"
						aria-selected={section === key}
						className={`sf-rap-subtab ${section === key ? "is-active" : ""}`}
						onClick={() => setSection(key)}
					>
						{t.sections[key]}
					</button>
				))}
			</nav>

			<div className={`sf-rap-sections is-${section}`}>
				<div className="sf-rap-sec" data-sec="overview">
					<div className="sf-rap-kpis">
						<Kpi
							t={t}
							primary
							icon={<CircleDollarSign size={15} />}
							label={t.kpis.revenue}
							hint={t.kpis.revenueHint}
							value={formatMoney(totals.completedRevenue, currency)}
							sub={`${totals.bookings.concluded} ${t.kpis.completed.toLowerCase()}`}
						/>
						<Kpi
							t={t}
							icon={<Clock3 size={15} />}
							label={t.kpis.projected}
							hint={t.kpis.projectedHint}
							value={formatMoney(totals.projectedRevenue, currency)}
							sub={`${pending}`}
						/>
						<Kpi
							t={t}
							icon={<Receipt size={15} />}
							label={t.kpis.averageTicket}
							hint={t.kpis.averageTicketHint}
							value={formatMoney(totals.averageTicket, currency)}
						/>
						<Kpi
							t={t}
							icon={<Users size={15} />}
							label={t.kpis.customers}
							hint={t.kpis.customersHint}
							value={totals.uniqueCustomers}
						/>
						{/* The count of cancellations MADE in this period, not the
						    outcome rate of this period's slots. Every other KPI in
						    this row is "what happened during these dates", and a
						    cohort rate sitting among them reads as the same thing
						    when it is not: it cannot see a customer cancelling next
						    month's appointment today, so it stays stubbornly low and
						    looks broken. The rate still exists, labelled for what it
						    measures, in the section below. */}
						<Kpi
							t={t}
							icon={<Ban size={15} />}
							label={t.kpis.cancellations}
							hint={activity ? t.kpis.cancellationsHint : t.kpis.cancellationsUnavailableHint}
							value={activity ? activity.count : "—"}
							sub={activity ? fill(t.kpis.cancellationsSub, { total: totals.bookings.total }) : undefined}
						/>
						{/* Churn's other half, and the reason the value-released
						    figure was not missed: this one names something a shop
						    can do something about. */}
						<Kpi
							t={t}
							icon={<PencilLine size={15} />}
							label={t.kpis.changes}
							hint={t.kpis.changesHint}
							value={modifications ? modifications.count : "—"}
							sub={
								modifications
									? fill(t.kpis.changesSub, {
											moved: modifications.rescheduled,
											edited: modifications.edited,
										})
									: undefined
							}
						/>
					</div>

					<section className="sf-rap-section">
						<div className="sf-shop-section-head">
							<h2 className="sf-shop-h2">{t.chart.title}</h2>
							<span className="sf-rap-head-note">{t.granularity[granularity]}</span>
						</div>
						<div className="sf-rap-panel">
							<RevenueChart
								series={series}
								granularity={granularity}
								currency={currency}
								language={language}
								t={t}
							/>
						</div>
					</section>
				</div>

				<div className="sf-rap-sec" data-sec="money">
					<section className="sf-rap-section">
						<div className="sf-shop-section-head">
							<h2 className="sf-shop-h2">{t.breakdown.title}</h2>
						</div>
						<div className="sf-rap-grid">
							<BreakdownPanel
								icon={<Scissors size={16} />}
								title={t.breakdown.byService}
								rows={byService}
								total={totals.completedRevenue}
								currency={currency}
								t={t}
								nameOf={(r) => r.name || r.serviceId}
							/>
							<BreakdownPanel
								icon={<UserRound size={16} />}
								title={t.breakdown.byStaff}
								rows={byStaff}
								total={totals.completedRevenue}
								currency={currency}
								t={t}
								nameOf={(r) => r.staffName || (r.staffId === "unassigned" ? t.breakdown.unassigned : r.staffId)}
							/>
							<BreakdownPanel
								icon={<TrendingUp size={16} />}
								title={t.breakdown.bySource}
								rows={bySource}
								total={totals.completedRevenue}
								currency={currency}
								t={t}
								nameOf={sourceLabel}
							/>
						</div>
					</section>
				</div>

				<div className="sf-rap-sec" data-sec="bookings">
					<BookingsSection volume={bookingVolume} uniqueCustomers={totals.uniqueCustomers} t={t} />
				</div>

				{/* activity.series and modifications.series (per-bucket counts) also
				    come back from the API, unused here — the KPIs and breakdowns
				    are what answer the question. Chart them if a trend view is
				    wanted. */}
				<div className="sf-rap-sec" data-sec="changes">
					<CancellationsSection cancellations={cancellations} t={t} />
					<ModificationsSection modifications={modifications} t={t} />
				</div>
			</div>

			{/* Outside the sections on purpose: a caveat about the figures should
			    not be reachable only by picking the right tab. */}
			{notices.length > 0 && (
				<section className="sf-rap-notice">
					<div className="sf-rap-notice-head">
						<AlertTriangle size={15} />
						{t.quality.title}
					</div>
					<ul>
						{notices.map((line, i) => (
							<li key={i}>{line}</li>
						))}
					</ul>
				</section>
			)}
		</>
	);
};

export default SalesPanel;
